import { Avatar, Box, Heading, Text, TooltipCommonProps } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { AIFilled } from '@wix/wix-ui-icons-common';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import ChevronLeftSmallFilled from '../../../../assets/img/ChevronLeftSmallFilled.svg';
import { BACKGROUND_DARK_COLOR } from '../../../../constants';
import { TLocalesKeys } from '../../../../types';
import { selectIsConnectingAgent, selectIsLiveChat } from '../../../common/selectors/chat';
import { selectOnlineAgent } from '../../../common/selectors/header';
import { FloatingBannerOrigin } from '../../../components/FloatingBanner/FloatingBanner';
import { StatusBanner } from '../../../components/StatusBanner';
import { goBackCopilot } from '../../../messageChannel/postNewMessages';
import { renderModel } from '../../../util/renderModel';
import { AnnouncementBanner } from './AnnouncementBanner';
import { headerFullPageDataHooks } from './HeaderFullPage.dataHooks';
import { HeaderFullPageActions } from './HeaderFullPageActions';
import style from './styles.scss';

const titleTooltipProps: TooltipCommonProps = {
  size: 'small',
  placement: 'bottom',
};

const isCopilot = renderModel.isCopilot();

export const HeaderFullPage: FC = () => {
  const { t } = useTranslation();
  const isLiveChat = useSelector(selectIsLiveChat);
  const agent = useSelector(selectOnlineAgent);
  const isConnectingAgent = useSelector(selectIsConnectingAgent);

  const titleSection = useMemo(() => {
    const shouldShowAgentInfo = isLiveChat && agent;

    return (
      <Box direction="vertical" gap="0.5">
        <Text size="tiny" weight="thin" light dataHook={headerFullPageDataHooks.title()}>
          {t<TLocalesKeys>('subheader_title')}
        </Text>
        {shouldShowAgentInfo ? (
          <Box gap="0.5" verticalAlign="middle">
            <Avatar
              size="size24"
              shape="square"
              name={agent.fullName}
              imgProps={{ src: agent.profileImage }}
              dataHook={headerFullPageDataHooks.agentAvatar()}
            />

            <Text
              light
              weight="bold"
              ellipsis
              tooltipProps={titleTooltipProps}
              dataHook={headerFullPageDataHooks.secondaryTitle()}
            >
              {agent.fullName}
            </Text>
          </Box>
        ) : (
          <Box gap="0.5" verticalAlign="middle">
            {isConnectingAgent ? (
              <Text light weight="bold">
                {t<TLocalesKeys>('header_title_connecting_agent')}
              </Text>
            ) : (
              <>
                <Text light>
                  <Box>
                    <AIFilled />
                  </Box>
                </Text>
                <Text light weight="bold">
                  {t<TLocalesKeys>('widget_title_ai_chatbot')}
                </Text>
              </>
            )}
          </Box>
        )}
      </Box>
    );
  }, [t, isLiveChat, agent, isConnectingAgent]);

  if (isCopilot) {
    return (
      <>
        <Box className={style.copilotAppHeader} align="space-between" paddingRight="SP2" marginBottom="SP2">
          <Box className={style.copilotAppHeaderTitleWrapper} gap="8px">
            <ChevronLeftSmallFilled onClick={goBackCopilot} className={style.chevronLeft} />
            <Heading
              className={style.copilotAppHeaderTitle}
              size="medium"
              dataHook={headerFullPageDataHooks.title()}
            >
              {t<TLocalesKeys>('widget_title_ai_chatbot')}
            </Heading>
          </Box>
          <HeaderFullPageActions />
        </Box>
        <AnnouncementBanner dataHook={headerFullPageDataHooks.announcementBanner()} />
        <StatusBanner />
      </>
    );
  }

  return (
    <>
      <Box
        className={style.contactAppHeader}
        align="space-between"
        verticalAlign="middle"
        backgroundColor={BACKGROUND_DARK_COLOR}
      >
        {titleSection}
        <HeaderFullPageActions />
      </Box>
      <AnnouncementBanner dataHook={headerFullPageDataHooks.announcementBanner()} />
      <StatusBanner origin={FloatingBannerOrigin.FullPage} />
    </>
  );
};
